/**
 * React hook for wallet onboarding and auth token generation
 * Single source of truth for all account-related information:
 * - Mnemonic phrase
 * - Wallet address (derived from mnemonic) - supports multichain
 * - Chain type for address generation
 * - Private key (computed from mnemonic, not stored) - Ethereum only
 * - Auth tokens (authToken, refreshToken, expiresAt)
 *
 * Provides onboarding functionality with loading and error states
 * Persists account data to localStorage
 * 
 * Note: Onboarding (auth token generation) is currently Ethereum-only (EIP-712),
 * but address generation supports all chain types.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ethers } from "ethers";
import { onboardAndGetAuthToken } from "../sdk/utils/onboard";
import type { ChainType } from "../sdk/types";
import { generateAddressFromMnemonic as generateAddressFromMnemonicUtil } from "../utils/addressGeneration";

export interface UseOnboardResult {
  // Account information (single source of truth)
  mnemonic: string;
  walletAddress: string;
  chainType: ChainType; // Chain type for the current wallet address
  privateKey: string; // Computed from mnemonic (Ethereum only), null if mnemonic not available

  // Auth tokens
  authToken: string;
  refreshToken?: string;
  expiresAt?: number;

  // State
  loading: boolean;
  error: string | null;
  addressLoading: boolean; // Loading state for address generation

  // Methods
  onboard: (
    mnemonic: string,
    baseURL: string,
    username?: string,
    referralCode?: string,
  ) => Promise<void>;
  setMnemonic: (mnemonic: string) => Promise<void>; // Set mnemonic and generate Ethereum address (backward compatible)
  setMnemonicWithChain: (
    mnemonic: string,
    chainType: ChainType,
    addressIndex?: number,
    isMainnet?: boolean,
  ) => Promise<void>; // Set mnemonic and generate address for specific chain type
  setAccountData: (mnemonic: string, address: string, chainType?: ChainType) => void; // Set both mnemonic and address directly
  generateAddressForChain: (
    chainType: ChainType,
    addressIndex?: number,
    isMainnet?: boolean,
  ) => Promise<string | null>; // Generate address for specific chain type
  clearAccountData: () => void; // Clear all account data
  clearError: () => void;
  reset: () => void; // Reset auth tokens only (keeps account data)
  resetAll: () => void; // Reset everything including account data
}

// localStorage keys
const STORAGE_KEY_MNEMONIC = "onboard_mnemonic";
const STORAGE_KEY_WALLET_ADDRESS = "onboard_wallet_address";
const STORAGE_KEY_CHAIN_TYPE = "onboard_chain_type"; // New: store chain type
const STORAGE_KEY_AUTH_TOKEN = "onboard_auth_token";
const STORAGE_KEY_REFRESH_TOKEN = "onboard_refresh_token";
const STORAGE_KEY_EXPIRES_AT = "onboard_expires_at";

/**
 * Helper function to infer chain type from address format
 * Used for automatic chain type detection when setting account data
 */
function inferChainTypeFromAddress(address: string): ChainType | null {
  const addr = address.trim();
  if (addr.startsWith("0x") || addr.startsWith("0X")) {
    return "ethereum";
  } else if (addr.startsWith("bc1p") || addr.startsWith("tb1p")) {
    return "bitcoin_taproot";
  } else if (addr.startsWith("bc1q") || addr.startsWith("tb1q")) {
    return "bitcoin_segwit";
  } else if (addr.startsWith("1") || addr.startsWith("m") || addr.startsWith("n")) {
    return "bitcoin_legacy";
  } else if (addr.startsWith("3") || addr.startsWith("2")) {
    return "bitcoin_nested_segwit";
  } else if (addr.length >= 32 && addr.length <= 44) {
    // Solana addresses are Base58, typically 32-44 characters
    // This is a heuristic - could be improved with actual Base58 validation
    return "solana";
  }
  return null;
}

/**
 * Hook for onboarding wallet and managing account information
 * Single source of truth for mnemonic, wallet address, private key, and auth tokens
 * @param initialBaseURL - Optional initial base URL (can be overridden in onboard call)
 * @returns UseOnboardResult with account state and methods
 */
export function useOnboard(_initialBaseURL?: string): UseOnboardResult {
  // Account state
  const [mnemonic, setMnemonicState] = useState<string>("");
  const [walletAddress, setWalletAddress] = useState<string>("");
  const [chainType, setChainType] = useState<ChainType>("ethereum"); // Default to Ethereum for backward compatibility
  const [addressLoading, setAddressLoading] = useState(false);

  // Auth token state
  const [authToken, setAuthToken] = useState<string>("");
  const [refreshToken, setRefreshToken] = useState<string | undefined>(
    undefined,
  );
  const [expiresAt, setExpiresAt] = useState<number | undefined>(undefined);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load persisted data from localStorage on mount
  useEffect(() => {
    const storedMnemonic = localStorage.getItem(STORAGE_KEY_MNEMONIC);
    const storedAddress = localStorage.getItem(STORAGE_KEY_WALLET_ADDRESS);
    const storedChainType = localStorage.getItem(STORAGE_KEY_CHAIN_TYPE);
    const storedAuthToken = localStorage.getItem(STORAGE_KEY_AUTH_TOKEN);
    const storedRefreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN);
    const storedExpiresAt = localStorage.getItem(STORAGE_KEY_EXPIRES_AT);

    if (storedMnemonic) {
      setMnemonicState(storedMnemonic);
    } else {
      setMnemonicState("");
    }
    if (storedAddress) {
      setWalletAddress(storedAddress);
    } else {
      setWalletAddress("");
    }
    if (storedChainType) {
      // Validate chain type
      const validChainTypes: ChainType[] = [
        "ethereum",
        "bitcoin_taproot",
        "bitcoin_segwit",
        "bitcoin_legacy",
        "bitcoin_nested_segwit",
        "solana",
      ];
      if (validChainTypes.includes(storedChainType as ChainType)) {
        setChainType(storedChainType as ChainType);
      } else {
        // Invalid chain type, default to Ethereum
        setChainType("ethereum");
        localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, "ethereum");
      }
    } else {
      // No stored chain type, default to Ethereum (backward compatibility)
      setChainType("ethereum");
    }
    if (storedAuthToken) {
      setAuthToken(storedAuthToken);
    }
    if (storedRefreshToken) {
      setRefreshToken(storedRefreshToken);
    }
    if (storedExpiresAt) {
      const expiresAtNum = parseInt(storedExpiresAt, 10);
      if (!isNaN(expiresAtNum)) {
        setExpiresAt(expiresAtNum);
      }
    }
  }, []);

  // Generate wallet address from mnemonic (Ethereum - backward compatible)
  // Uses shared address generation utility to eliminate duplication
  const generateAddressFromMnemonic = useCallback(
    async (mnemonicValue: string): Promise<string | null> => {
      if (!mnemonicValue || mnemonicValue.trim() === "") {
        return null;
      }

      setAddressLoading(true);
      try {
        const result = await generateAddressFromMnemonicUtil(
          mnemonicValue,
          "ethereum",
          0,
          true,
        );

        if (result.success && result.address) {
          return result.address;
        } else {
          console.error("Failed to generate address:", result.error);
          return null;
        }
      } catch (err: any) {
        console.error("Error generating address:", err);
        return null;
      } finally {
        setAddressLoading(false);
      }
    },
    [],
  );

  // Generate address for specific chain type (multichain support)
  // Uses shared address generation utility to eliminate duplication
  const generateAddressForChain = useCallback(
    async (
      targetChainType: ChainType,
      addressIndex: number = 0,
      isMainnet: boolean = true,
    ): Promise<string | null> => {
      if (!mnemonic || mnemonic.trim() === "") {
        console.warn("Cannot generate address: mnemonic not set");
        return null;
      }

      setAddressLoading(true);
      try {
        const result = await generateAddressFromMnemonicUtil(
          mnemonic,
          targetChainType,
          addressIndex,
          isMainnet,
        );

        if (result.success && result.address) {
          // Update chain type state
          setChainType(targetChainType);
          localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, targetChainType);
          return result.address;
        } else {
          console.error("Failed to generate address:", result.error);
          return null;
        }
      } catch (err: any) {
        console.error("Error generating address for chain:", err);
        return null;
      } finally {
        setAddressLoading(false);
      }
    },
    [mnemonic],
  );

  // Set mnemonic and generate Ethereum address (backward compatible)
  const setMnemonic = useCallback(
    async (mnemonicValue: string) => {
      const trimmedMnemonic = mnemonicValue.trim();

      if (trimmedMnemonic === "") {
        // Clear account data if empty mnemonic
        setMnemonicState("");
        setWalletAddress("");
        setChainType("ethereum");
        localStorage.removeItem(STORAGE_KEY_MNEMONIC);
        localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
        localStorage.removeItem(STORAGE_KEY_CHAIN_TYPE);
        return;
      }

      setMnemonicState(trimmedMnemonic);
      localStorage.setItem(STORAGE_KEY_MNEMONIC, trimmedMnemonic);

      // Generate Ethereum address from mnemonic (backward compatible)
      const address = await generateAddressFromMnemonic(trimmedMnemonic);
      if (address) {
        setWalletAddress(address);
        setChainType("ethereum");
        localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, address);
        localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, "ethereum");
      } else {
        // Clear address if generation failed
        setWalletAddress("");
        setChainType("ethereum");
        localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
        localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, "ethereum");
      }
    },
    [generateAddressFromMnemonic],
  );

  // Set mnemonic and generate address for specific chain type (multichain support)
  const setMnemonicWithChain = useCallback(
    async (
      mnemonicValue: string,
      targetChainType: ChainType,
      addressIndex: number = 0,
      isMainnet: boolean = true,
    ) => {
      const trimmedMnemonic = mnemonicValue.trim();

      if (trimmedMnemonic === "") {
        // Clear account data if empty mnemonic
        setMnemonicState("");
        setWalletAddress("");
        setChainType("ethereum");
        localStorage.removeItem(STORAGE_KEY_MNEMONIC);
        localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
        localStorage.removeItem(STORAGE_KEY_CHAIN_TYPE);
        return;
      }

      setMnemonicState(trimmedMnemonic);
      localStorage.setItem(STORAGE_KEY_MNEMONIC, trimmedMnemonic);

      // Generate address for the specified chain type
      const address = await generateAddressForChain(
        targetChainType,
        addressIndex,
        isMainnet,
      );
      if (address) {
        setWalletAddress(address);
        setChainType(targetChainType);
        localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, address);
        localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, targetChainType);
      } else {
        // Clear address if generation failed
        setWalletAddress("");
        localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
      }
    },
    [generateAddressForChain],
  );

  // Set both mnemonic and address directly (useful when address is already known)
  // Optionally accepts chainType to store chain information
  const setAccountData = useCallback(
    (mnemonicValue: string, address: string, addressChainType?: ChainType) => {
      const trimmedMnemonic = mnemonicValue.trim();
      // Only lowercase Ethereum addresses (0x...), preserve case for other chains
      const trimmedAddress =
        address.trim().startsWith("0x") || address.trim().startsWith("0X")
          ? address.trim().toLowerCase()
          : address.trim();

      setMnemonicState(trimmedMnemonic);
      setWalletAddress(trimmedAddress);
      localStorage.setItem(STORAGE_KEY_MNEMONIC, trimmedMnemonic);
      localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, trimmedAddress);

      // If chain type is provided, store it; otherwise try to infer from address format
      if (addressChainType) {
        setChainType(addressChainType);
        localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, addressChainType);
      } else {
        // Try to infer chain type from address format
        const inferredChainType = inferChainTypeFromAddress(trimmedAddress);
        if (inferredChainType) {
          setChainType(inferredChainType);
          localStorage.setItem(STORAGE_KEY_CHAIN_TYPE, inferredChainType);
        }
      }
    },
    [],
  );

  // Clear all account data
  const clearAccountData = useCallback(() => {
    setMnemonicState("");
    setWalletAddress("");
    setChainType("ethereum");
    localStorage.removeItem(STORAGE_KEY_MNEMONIC);
    localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
    localStorage.removeItem(STORAGE_KEY_CHAIN_TYPE);
  }, []);

  // Compute private key from mnemonic (not stored, computed on demand)
  const privateKey = useMemo((): string => {
    if (mnemonic === "") {
      return "";
    }
    const trimmedMnemonic = mnemonic.trim();
    if (trimmedMnemonic === "") {
      return "";
    }

    try {
      // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
      // This matches the same derivation used in onboard.ts and ExchangeClient
      const masterNode = ethers.HDNodeWallet.fromPhrase(
        trimmedMnemonic,
        "", // no passphrase
        "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
      );

      // Validate that derived address matches stored address if available
      if (
        walletAddress &&
        masterNode.address.toLowerCase() !== walletAddress.toLowerCase()
      ) {
        console.warn(
          "Address mismatch: derived address does not match stored address",
        );
        return "";
      }

      // Extract private key in the exact format Go's crypto.ToECDSA expects
      const privateKeyBigInt = BigInt(masterNode.privateKey);
      const SECP256K1_ORDER = BigInt(
        "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
      );

      if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
        console.error("Invalid private key: out of secp256k1 curve order");
        return "";
      }

      // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
      const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
      const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

      if (!/^[0-9a-f]{64}$/.test(paddedHex)) {
        console.error("Invalid private key format");
        return "";
      }

      return paddedHex;
    } catch (err: any) {
      console.error("Error computing private key:", err);
      return "";
    }
  }, [mnemonic, walletAddress]);

  // Onboard with mnemonic
  const onboard = useCallback(
    async (
      mnemonicValue: string,
      baseURL: string,
      username?: string,
      referralCode?: string,
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        // First, set the mnemonic and generate address
        await setMnemonic(mnemonicValue);

        // Then perform onboarding
        const result = await onboardAndGetAuthToken(
          mnemonicValue,
          baseURL,
          username,
          referralCode,
        );

        if (result.success && result.authToken) {
          setAuthToken(result.authToken);
          setRefreshToken(result.refreshToken);
          setExpiresAt(result.expiresAt);

          // Persist auth tokens to localStorage
          localStorage.setItem(STORAGE_KEY_AUTH_TOKEN, result.authToken);
          if (result.refreshToken) {
            localStorage.setItem(
              STORAGE_KEY_REFRESH_TOKEN,
              result.refreshToken,
            );
          }
          if (result.expiresAt) {
            localStorage.setItem(
              STORAGE_KEY_EXPIRES_AT,
              result.expiresAt.toString(),
            );
          }

          toast.success("Onboarding successful", {
            description: "Wallet authenticated and auth token generated.",
            duration: 5000,
          });
        } else {
          const errorMsg = result.error || "Onboarding failed";
          setError(errorMsg);
          setAuthToken("");
          setRefreshToken(undefined);
          setExpiresAt(undefined);
          localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
          toast.error("Onboarding failed", {
            description: errorMsg,
            duration: 6000,
          });
        }
      } catch (err: any) {
        const errorMsg = err?.message || "Unknown error during onboarding";
        setError(errorMsg);
        setAuthToken("");
        setRefreshToken(undefined);
        setExpiresAt(undefined);
        localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
        toast.error("Onboarding error", {
          description: errorMsg,
          duration: 6000,
        });
      } finally {
        setLoading(false);
      }
    },
    [setMnemonic],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset auth tokens only (keeps account data)
  const reset = useCallback(() => {
    setAuthToken("");
    setRefreshToken(undefined);
    setExpiresAt(undefined);
    setError(null);
    setLoading(false);
    localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
  }, []);

  // Reset everything including account data
  const resetAll = useCallback(() => {
    clearAccountData();
    reset();
  }, [clearAccountData, reset]);

  return {
    // Account information
    mnemonic,
    walletAddress,
    chainType,
    privateKey,

    // Auth tokens
    authToken,
    refreshToken,
    expiresAt,

    // State
    loading,
    error,
    addressLoading,

    // Methods
    onboard,
    setMnemonic,
    setMnemonicWithChain,
    setAccountData,
    generateAddressForChain,
    clearAccountData,
    clearError,
    reset,
    resetAll,
  };
}
